import { SUPPLIERS_TYPES_IDS, invariant } from '@wyz/utils';
import {
  REDISTRIBUTION_RULE_STATUSES,
  RedistributionRuleStatus,
  SUPPLIERS_STATUSES,
} from '~/connect/schemas';
import {
  ICriteriaList,
  IRedistributionSupplierTemplate,
  IRedistributionSupplierTemplateForm,
  IRedistributionBusinessRulesForm,
  IRedistributionSupplierTemplatePayload,
} from '~/connect/types';

export const tryReorderSuppliers = (
  suppliers: IRedistributionSupplierTemplateForm['suppliers'],
  from: number,
  to: number,
): IRedistributionSupplierTemplateForm['suppliers'] | null => {
  const item = suppliers[from];
  if (!item || !item._internalStock) return null;

  const newSuppliers = [...suppliers];
  newSuppliers.splice(from, 1);
  newSuppliers.splice(to, 0, item);

  const firstTemplateIndex = newSuppliers.findIndex((s) => !s._internalStock);
  if (firstTemplateIndex === -1) return newSuppliers;

  const lastTemplateIndex =
    newSuppliers.length -
    1 -
    [...newSuppliers].reverse().findIndex((s) => !s._internalStock);

  const hasInternalStockBetweenTemplates = newSuppliers
    .slice(firstTemplateIndex, lastTemplateIndex + 1)
    .some((s) => s._internalStock);

  if (hasInternalStockBetweenTemplates) return null;

  return newSuppliers;
};

type FormSuppliers = IRedistributionSupplierTemplateForm['suppliers'];

const INTERNAL_STOCK_SUPPLIER_TYPES: Array<number> = [
  SUPPLIERS_TYPES_IDS.PLATFORM_DISTRIBUTOR,
  SUPPLIERS_TYPES_IDS.CENTRAL_STOCK,
];

const isInternalStockSupplier = (supplier: FormSuppliers[number]): boolean =>
  INTERNAL_STOCK_SUPPLIER_TYPES.includes(supplier.type);

// Which per-supplier flag each rule drives, and over which suppliers. Mirrors
// RedistributionBusinessRuleResolver on the API side; the DMS rules are absent on purpose, they
// carry no supplier impact.
const SUPPLIER_BACKED_RULES = {
  autoValidationEnabled: { field: 'autoValidation', scope: 'ALL' },
  internalOrderValidation: { field: 'autoValidation', scope: 'INTERNAL' },
  externalOrderValidation: { field: 'autoValidation', scope: 'EXTERNAL' },
  directStoreDelivery: { field: 'childCustomerDelivery', scope: 'ALL' },
  internalDirectStoreDelivery: {
    field: 'childCustomerDelivery',
    scope: 'INTERNAL',
  },
  externalDirectStoreDelivery: {
    field: 'childCustomerDelivery',
    scope: 'EXTERNAL',
  },
} as const;

const isInScope = (
  supplier: FormSuppliers[number],
  scope: 'ALL' | 'INTERNAL' | 'EXTERNAL',
): boolean => {
  if (scope === 'ALL') return true;

  return scope === 'INTERNAL'
    ? isInternalStockSupplier(supplier)
    : !isInternalStockSupplier(supplier);
};

const statusFromFlags = (flags: Array<boolean>): RedistributionRuleStatus => {
  if (flags.length === 0) return REDISTRIBUTION_RULE_STATUSES.DISABLED;

  if (flags.every(Boolean)) return REDISTRIBUTION_RULE_STATUSES.ENABLED;

  return flags.some(Boolean)
    ? REDISTRIBUTION_RULE_STATUSES.INTERMEDIATE
    : REDISTRIBUTION_RULE_STATUSES.DISABLED;
};

// One rule -> its own scope. Toggling a rule must never touch the suppliers another rule governs,
// so this takes the single status rather than the whole set, which would re-apply stale values.
export const applyBusinessRuleToSuppliers = (
  suppliers: FormSuppliers,
  field: keyof IRedistributionBusinessRulesForm,
  status: RedistributionRuleStatus,
): FormSuppliers => {
  const rule =
    SUPPLIER_BACKED_RULES[field as keyof typeof SUPPLIER_BACKED_RULES];

  // The DMS rules have no supplier counterpart, and INTERMEDIATE is a result, never an input.
  if (!rule || status === REDISTRIBUTION_RULE_STATUSES.INTERMEDIATE) {
    return suppliers;
  }

  return suppliers.map((supplier) =>
    isInScope(supplier, rule.scope)
      ? {
          ...supplier,
          [rule.field]: status === REDISTRIBUTION_RULE_STATUSES.ENABLED,
        }
      : supplier,
  );
};

// The whole set -> suppliers, for seeding a fresh supplier list from the platform's generic rule.
// INTERMEDIATE is a result, never an input: it leaves its scope untouched.
export const applyBusinessRulesToSuppliers = (
  suppliers: FormSuppliers,
  businessRules: IRedistributionBusinessRulesForm,
): FormSuppliers =>
  Object.entries(SUPPLIER_BACKED_RULES).reduce<FormSuppliers>(
    (accumulator, [rule, { field, scope }]) => {
      const status = businessRules[rule as keyof typeof SUPPLIER_BACKED_RULES];

      if (status === REDISTRIBUTION_RULE_STATUSES.INTERMEDIATE) {
        return accumulator;
      }

      return accumulator.map((supplier) =>
        isInScope(supplier, scope)
          ? {
              ...supplier,
              [field]: status === REDISTRIBUTION_RULE_STATUSES.ENABLED,
            }
          : supplier,
      );
    },
    suppliers,
  );

// A rule that owns sub-rules is only intermediate when a sub-rule is: one sub-rule on and the other
// off still leaves the parent on.
const parentStatus = (
  ...children: Array<RedistributionRuleStatus>
): RedistributionRuleStatus => {
  if (children.includes(REDISTRIBUTION_RULE_STATUSES.INTERMEDIATE)) {
    return REDISTRIBUTION_RULE_STATUSES.INTERMEDIATE;
  }

  return children.includes(REDISTRIBUTION_RULE_STATUSES.ENABLED)
    ? REDISTRIBUTION_RULE_STATUSES.ENABLED
    : REDISTRIBUTION_RULE_STATUSES.DISABLED;
};

// Suppliers -> rule. The DMS rules are passed through: nothing on a supplier can contradict them.
export const deriveBusinessRulesFromSuppliers = (
  suppliers: FormSuppliers,
  businessRules: IRedistributionBusinessRulesForm,
): IRedistributionBusinessRulesForm => {
  if (suppliers.length === 0) return businessRules;

  const derived = Object.entries(SUPPLIER_BACKED_RULES).reduce(
    (accumulator, [rule, { field, scope }]) => ({
      ...accumulator,
      [rule]: statusFromFlags(
        suppliers
          .filter((supplier) => isInScope(supplier, scope))
          .map((supplier) => Boolean(supplier[field])),
      ),
    }),
    {},
  );

  const rules = { ...businessRules, ...derived };

  return {
    ...rules,
    autoValidationEnabled: parentStatus(
      rules.internalOrderValidation,
      rules.externalOrderValidation,
    ),
    directStoreDelivery: parentStatus(
      rules.internalDirectStoreDelivery,
      rules.externalDirectStoreDelivery,
    ),
  };
};

const getCriterionValuesByIds = (
  criterionId: string,
  criterionValueIds: Array<string>,
  allCriteria: ICriteriaList['data'],
): IRedistributionSupplierTemplatePayload['criteria'][number]['values'] => {
  const criterion = allCriteria.find((item) => item.id === criterionId);
  invariant(criterion);
  return criterion.values.filter((criterionValue) =>
    criterionValueIds.includes(criterionValue.id),
  );
};

export const rawToForm = (
  document: IRedistributionSupplierTemplate,
): IRedistributionSupplierTemplateForm => {
  return {
    ...document,
    customerScope: document.customerScope?.at(0)?.id || '',
    criteria: document.criteria.map((associatedCriterion) => ({
      ...associatedCriterion,
      values: associatedCriterion.values.map(
        (associatedCriterionValue) => associatedCriterionValue.id,
      ),
    })),
    suppliers: document.suppliers.map((supplier) => ({
      code: supplier.code ?? null,
      wyzApiId: supplier.wyzApiId,
      name: supplier.name ?? '',
      type: supplier.type,
      orderable: supplier.orderable,
      overridePriorityIfShorterDelivery:
        supplier.overridePriorityIfShorterDelivery ?? null,
      customName: supplier.customName,
      status: supplier.status,
      enabled: supplier.status === SUPPLIERS_STATUSES.ENABLED,
      rankingModelId: supplier.rankingModelId ?? null,
      childCustomerDelivery: supplier.childCustomerDelivery ?? false,
      autoValidation: supplier.autoValidation ?? false,
      bestPriceDelay: supplier.bestPriceDelay ?? null,
      extendedManufConfiguration: supplier.extendedManufConfiguration ?? null,
      customerId: supplier.customerId ?? null,
      _internalStock: supplier._internalStock ?? false,
    })),
  };
};
export const formToPayload = (
  formData: IRedistributionSupplierTemplateForm,
  allCriteria: ICriteriaList['data'],
): IRedistributionSupplierTemplatePayload => {
  return {
    name: formData.name,
    businessRules: deriveBusinessRulesFromSuppliers(
      formData.suppliers ?? [],
      formData.businessRules,
    ),
    customerScope: formData.customerScope
      ? [{ id: formData.customerScope }]
      : [],
    criteria: formData.criteria.map((associatedCriterion) => ({
      ...associatedCriterion,
      values: getCriterionValuesByIds(
        associatedCriterion.criterion.id,
        associatedCriterion.values,
        allCriteria,
      ),
    })),
    suppliers: (formData.suppliers ?? []).map((supplier) => ({
      wyzApiId: supplier.wyzApiId,
      orderable: supplier.orderable ?? true,
      customName: supplier.customName,
      type: supplier.type,
      status:
        supplier.enabled === true
          ? SUPPLIERS_STATUSES.ENABLED
          : SUPPLIERS_STATUSES.DISABLED,
      customerId: supplier.customerId ?? null,
      overridePriorityIfShorterDelivery:
        supplier.overridePriorityIfShorterDelivery ?? true,
      rankingModelId: supplier.rankingModelId ?? null,
      childCustomerDelivery: supplier.childCustomerDelivery ?? false,
      autoValidation: supplier.autoValidation ?? false,
      extendedManufConfiguration: supplier.extendedManufConfiguration
        ? {
            enabled: supplier.extendedManufConfiguration.enabled ?? false,
            warehouseId: supplier.extendedManufConfiguration.warehouseId ?? '',
            displayName: supplier.extendedManufConfiguration.displayName ?? '',
          }
        : null,
    })),
  };
};
